import { DataHelpers } from "@/core/modules/helpers/DataHelpers";
import { DocumentPaper } from "@/features/modules/paper/objects/DocumentPaper";
import { ParcelWithAction } from "../objects/ParcelWithAction";

export class HandleReportPaper extends DocumentPaper {
  private companyName: string | undefined;
  private monthDate: Date;
  private results: ParcelWithAction[];
  private handleReportCount: number;
  private handleReportInitialCount: number;
  private handleReportPeak: number;

  constructor(
    companyName: string | undefined,
    monthDate: Date,
    results: ParcelWithAction[],
    handleReportCount: number,
    handleReportInitialCount: number,
    handleReportPeak: number,
    d: (date: Date, format: string) => string,
    n: (value: number, format: string) => string,
    t: (entry: string, params?: Record<string, unknown>) => string
  ) {
    super(d, n, t);
    this.companyName = companyName;
    this.monthDate = monthDate;
    this.results = results;
    this.handleReportCount = handleReportCount;
    this.handleReportInitialCount = handleReportInitialCount;
    this.handleReportPeak = handleReportPeak;
  }

  public setStyles(): Record<string, unknown> {
    this.docDefinition.styles = {
      ...super.setStyles(),
      handleReportDetail: { fontSize: 10 },
      handleReportParcelCell: { alignment: "center", fontSize: 8 },
      handleReportParcelCellLeft: { alignment: "left", fontSize: 8 },
      handleReportParcelFoot: { alignment: "center", bold: true, color: "white", fillColor: "gray", fontSize: 8 },
      handleReportParcelFootRight: { alignment: "right", bold: true, color: "white", fillColor: "gray", fontSize: 8 },
      handleReportParcelHead: { alignment: "center", bold: true, color: "white", fillColor: "gray", fontSize: 10 },
    };

    return this.docDefinition.styles as Record<string, unknown>;
  }

  public async write(): Promise<Record<string, unknown>[]> {
    await this.setStructure(this.t("statHandleReport.handleReportPaper").toLocaleUpperCase());

    this.writeDetails();

    this.writeHandleReportTable();

    return this.docDefinition.content as Record<string, unknown>[];
  }

  private writeDetails(): void {
    (this.docDefinition.content as Record<string, unknown>[]).push({
      columns: [
        {
          width: DataHelpers.mmToPoints(40),
          stack: [
            {
              margin: [0, DataHelpers.mmToPoints(4), 0, 0],
              style: "handleReportDetail",
              text: `${this.t("statHandleReport.company").toLocaleUpperCase()}:`,
            },
            {
              margin: [0, DataHelpers.mmToPoints(2), 0, 0],
              style: "handleReportDetail",
              text: `${this.t("statHandleReport.month").toLocaleUpperCase()}:`,
            },
          ],
        },
        {
          width: "*",
          stack: [
            { margin: [0, DataHelpers.mmToPoints(4), 0, 0], style: "handleReportDetail", text: this.companyName ?? "-" },
            {
              margin: [0, DataHelpers.mmToPoints(2), 0, 0],
              style: "handleReportDetail",
              text: this.d(this.monthDate, "longMonth"),
            },
          ],
        },
      ],
    });
  }

  private writeHandleReportTable(): void {
    const dataTableBody: unknown[] = [
      [
        { style: "handleReportParcelHead", text: this.t("statHandleReport.date").toLocaleUpperCase() },
        { style: "handleReportParcelHead", text: this.t("statHandleReport.parcel").toLocaleUpperCase() },
        { style: "handleReportParcelHead", text: this.t("statHandleReport.shipment").toLocaleUpperCase() },
        { style: "handleReportParcelHead", text: this.t("statHandleReport.action").toLocaleUpperCase() },
        { style: "handleReportParcelHead", text: this.t("statHandleReport.count").toLocaleUpperCase() },
      ],
    ];

    for (const result of this.results) {
      dataTableBody.push([
        {
          style: "handleReportParcelCellLeft",
          text: this.d(result.date || new Date(), "shortDate"),
        },
        {
          style: "handleReportParcelCellLeft",
          text: result.type
            ? `${result.type.name} ${result.code}\n${result.companyCodes}`
            : `${this.t("statHandleReport.parcel")} ${result.code}\n${result.companyCodes}`,
        },
        {
          style: "handleReportParcelCellLeft",
          text: result.shipment
            ? this.t("statHandleReport.shipmentDetail", {
                code: result.shipment.code,
                date: this.d(result.shipment.date || new Date(), "shortDate"),
                companyName: result.shipment.company?.name ?? "-",
              })
            : "-",
        },
        {
          style: "handleReportParcelCell",
          text:
            result.action == 1
              ? "+1"
              : result.action == -1
              ? "-1"
              : this.t("statHandleReport.parcelStates.unknown").toLocaleUpperCase(),
        },
        {
          style: "handleReportParcelCell",
          text: this.n(result.count ?? 0, "number0"),
        },
      ]);
    }

    if (dataTableBody.length === 1) {
      dataTableBody.push([{ colSpan: 4, style: "handleReportParcelCell", text: this.t("gen.noResults") }]);
    } else {
      dataTableBody.push([
        { style: "handleReportParcelFootRight", colSpan: 4, text: this.t("statHandleReport.handleReportInitialCount").toLocaleUpperCase() },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: this.n(this.handleReportInitialCount, "number0") },
      ]);
      dataTableBody.push([
        { style: "handleReportParcelFootRight", colSpan: 4, text: this.t("statHandleReport.handleReportCount").toLocaleUpperCase() },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: this.n(this.handleReportCount, "number0") },
      ]);
      dataTableBody.push([
        { style: "handleReportParcelFootRight", colSpan: 4, text: this.t("statHandleReport.handleReportPeak").toLocaleUpperCase() },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: "" },
        { style: "handleReportParcelFoot", text: this.n(this.handleReportPeak, "number0") },
      ]);
    }

    (this.docDefinition.content as Record<string, unknown>[]).push({
      margin: [0, DataHelpers.mmToPoints(5), 0, 0],
      table: {
        body: dataTableBody,
        headerRows: 1,
        widths: [DataHelpers.mmToPoints(25), DataHelpers.mmToPoints(30), "*", DataHelpers.mmToPoints(18), DataHelpers.mmToPoints(18)],
      },
      layout: {
        hLineWidth: () => 0.5,
        hLineColor: () => "black",
      },
    });
  }
}
